import type { ReadReceipt } from '@/types/referral-outbound-center/models/ReadReceipt';

export interface ThreadMessage {
  message_id: string;
  sender_user_id: string | null;
  sender_facility_id: string;
  receiver_facility_id: string;
}

export const belongsToThread = (
  m: ThreadMessage,
  recipientFacilityId: string
): boolean =>
  m.sender_facility_id === recipientFacilityId ||
  m.receiver_facility_id === recipientFacilityId;

export interface ReadState {
  firstReadAt: string | null;
}

export const computeReadState = (
  message: ThreadMessage,
  receipts: ReadReceipt[],
  recipientFacilityId: string,
  currentUserId: string
): ReadState | null => {
  if (message.sender_user_id !== currentUserId) {
    return null;
  }

  const eligible = receipts.filter(
    (r) =>
      r.recipient_facility_id === recipientFacilityId &&
      r.user_id !== currentUserId &&
      // ULID lex order: last_read_message_id >= message_id means read.
      r.last_read_message_id >= message.message_id
  );
  if (eligible.length === 0) {
    return null;
  }

  const firstReadAt = eligible.map((r) => r.last_read_at).sort()[0];
  return { firstReadAt: firstReadAt ?? null };
};

export const shouldMarkAsRead = (
  latestMessageId: string,
  receipts: ReadReceipt[],
  recipientFacilityId: string,
  currentUserId: string
): boolean => {
  if (!latestMessageId || !currentUserId) {
    return false;
  }
  const myReceipt = receipts.find(
    (r) =>
      r.user_id === currentUserId &&
      r.recipient_facility_id === recipientFacilityId
  );
  if (!myReceipt) {
    return true;
  }
  return latestMessageId > myReceipt.last_read_message_id;
};

export const isThreadUnread = (
  messages: ThreadMessage[],
  receipts: ReadReceipt[],
  recipientFacilityId: string,
  currentUserId: string
): boolean => {
  const inThread = messages.filter((m) =>
    belongsToThread(m, recipientFacilityId)
  );
  const latest = inThread[inThread.length - 1];
  if (!latest) {
    return false;
  }
  if (latest.sender_user_id === currentUserId) {
    return false;
  }
  const myReceipt = receipts.find(
    (r) =>
      r.user_id === currentUserId &&
      r.recipient_facility_id === recipientFacilityId
  );
  if (!myReceipt) {
    return true;
  }
  return latest.message_id > myReceipt.last_read_message_id;
};

export const selectLatestSentId = (
  messages: ThreadMessage[],
  currentUserId: string
): string | null => {
  if (!currentUserId) {
    return null;
  }
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (!m) {
      continue;
    }
    if (m.sender_user_id === currentUserId) {
      return m.message_id;
    }
  }
  return null;
};
